import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { useUser } from '@/lib/auth';
import { RouteLoader } from '@/components/ui/feedback/route-loader';
import { ROUTES } from '@/config/routes';

/**
 * ProtectedRoute - Authentication Boundary
 *
 * Simplified component that ONLY handles authentication (not authorization).
 * Authorization is handled by RoleGuard at route-group level.
 *
 * CRITICAL: Requires both user AND session to be present.
 * When email confirmation is enabled, registerFn returns user but session=null.
 * Without the session check, unverified users could access protected routes.
 *
 * Pattern: Bulletproof React architecture
 * Reference: /docs/taskmaster/reports/holding/AUTH_AUTHORIZATION_REQUIREMENTS_FINAL.md
 */

interface ProtectedRouteProps {
  children?: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { data: auth, isLoading } = useUser();
  const location = useLocation();

  if (isLoading) {
    return <RouteLoader variant="full" />;
  }

  // CRITICAL: Check for both user AND session
  // Session is null when email confirmation is required but not yet completed
  // Without session, the user cannot make authenticated API calls
  if (!auth?.user || !auth?.session) {
    return <Navigate to={ROUTES.SIGN_IN} state={{ from: location }} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};
